import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';
import RelatedServices from '../../../components/feature/RelatedServices';
import RelatedIndustries from '../../../components/feature/RelatedIndustries';

export default function SocialMediaMarketingPage() {
  const features = [
    'Social Media Strategy',
    'Content Creation & Design',
    'Post Scheduling & Publishing',
    'Community Management',
    'Paid Social Advertising',
    'Influencer Partnerships',
    'Social Media Analytics',
    'Reputation Monitoring',
    'Patient Engagement',
    'Video Content Production',
    'Story & Reel Creation',
    'Monthly Performance Reports'
  ];

  const platforms = [
    {
      name: 'Facebook',
      icon: 'ri-facebook-fill',
      description: 'Build community, share educational content, and run targeted ads to reach local patients.',
      color: 'from-blue-600 to-blue-700'
    },
    {
      name: 'Instagram',
      icon: 'ri-instagram-line',
      description: 'Visual storytelling through posts, stories, and reels that showcase your practice and expertise.',
      color: 'from-pink-600 to-purple-600'
    },
    {
      name: 'LinkedIn',
      icon: 'ri-linkedin-fill',
      description: 'Professional networking, thought leadership, and B2B connections with referring providers.',
      color: 'from-blue-700 to-blue-800'
    },
    {
      name: 'YouTube',
      icon: 'ri-youtube-fill',
      description: 'Educational video content that builds trust and ranks in both YouTube and Google search.',
      color: 'from-red-600 to-red-700'
    }
  ];

  const benefits = [
    {
      icon: 'ri-user-add-line',
      title: 'Attract New Patients',
      description: 'Reach potential patients where they spend time daily and guide them to book appointments.'
    },
    {
      icon: 'ri-heart-line',
      title: 'Build Patient Relationships',
      description: 'Engage with current and prospective patients, answer questions, and build lasting connections.'
    },
    {
      icon: 'ri-megaphone-line',
      title: 'Increase Brand Awareness',
      description: 'Stay top-of-mind with consistent, valuable content that showcases your expertise and personality.'
    },
    {
      icon: 'ri-user-voice-line',
      title: 'Establish Thought Leadership',
      description: 'Position yourself as the go-to expert in your specialty through educational and engaging content.'
    },
    {
      icon: 'ri-share-forward-line',
      title: 'Amplify Your Reach',
      description: 'Social sharing extends your content reach far beyond your immediate followers and website visitors.'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'HIPAA-Compliant Strategy',
      description: 'All content and engagement follows strict healthcare privacy guidelines and best practices.'
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Strategy Development',
      description: 'Define goals, target audience, content themes, and platform strategy aligned with your practice.'
    },
    {
      step: '02',
      title: 'Content Planning',
      description: 'Create a monthly content calendar with diverse post types, topics, and engagement opportunities.'
    },
    {
      step: '03',
      title: 'Content Creation',
      description: 'Design graphics, write copy, produce videos, and create engaging content that resonates with patients.'
    },
    {
      step: '04',
      title: 'Publishing & Scheduling',
      description: 'Schedule posts at optimal times for maximum reach and engagement across all platforms.'
    },
    {
      step: '05',
      title: 'Community Management',
      description: 'Monitor comments, messages, and mentions, responding promptly and professionally to all engagement.'
    },
    {
      step: '06',
      title: 'Analytics & Optimization',
      description: 'Track performance metrics and continuously refine strategy based on what resonates with your audience.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 lg:px-12 bg-gradient-to-br from-[#F5F3FF] via-white to-[#FAF5FF] relative overflow-hidden">
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#7C3AED] font-bold mb-4">
                SOCIAL MEDIA MARKETING
              </div>
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
                Engage Patients &amp; Build Your Brand on Social Media
              </h1>
              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                Strategic social media management that builds awareness, engages patients, and drives appointment bookings through HIPAA-compliant content and community building.
              </p>
              <div className="flex flex-wrap gap-4">
                <a
                  href="/book-strategy-call"
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-[#7C3AED] to-[#A855F7] text-white px-8 py-4 rounded-lg hover:shadow-lg transition-all cursor-pointer whitespace-nowrap font-bold"
                >
                  <span>Get Your Social Strategy</span>
                  <i className="ri-arrow-right-line"></i>
                </a>
                <a
                  href="#process"
                  className="inline-flex items-center gap-2 bg-white text-[#7C3AED] border-2 border-[#7C3AED] px-8 py-4 rounded-lg hover:bg-[#7C3AED]/5 transition-all cursor-pointer whitespace-nowrap font-medium"
                >
                  <span>See Our Process</span>
                </a>
              </div>
            </div>
            <div className="relative">
              <div className="w-full h-[500px] rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://public.readdy.ai/ai/img_res/b8313947fbf44127bd2b4b58ae5d9470.jpg"
                  alt="Social media marketing for healthcare"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Platforms Section */}
      <section className="py-24 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="text-xs uppercase tracking-[0.2em] text-[#7C3AED] font-bold mb-4">
              PLATFORMS WE MANAGE
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
              Be Where Your Patients Are
            </h2>
            <p className="text-base text-gray-600 max-w-3xl mx-auto">
              We manage your presence across all major social platforms with platform-specific strategies and content.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {platforms.map((platform, index) => (
              <div
                key={index}
                className="bg-[#F9FAFB] rounded-xl p-10 hover:shadow-xl transition-all duration-300 border border-gray-100"
              >
                <div className={`w-16 h-16 bg-gradient-to-br ${platform.color} rounded-xl flex items-center justify-center mb-6`}>
                  <i className={`${platform.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {platform.name}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {platform.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 px-6 lg:px-12 bg-[#F9FAFB]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="text-xs uppercase tracking-[0.2em] text-[#7C3AED] font-bold mb-4">
              WHY SOCIAL MEDIA MATTERS
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
              Connect with Patients Where They Spend Time
            </h2>
            <p className="text-base text-gray-600 max-w-3xl mx-auto">
              Social media is where patients discover providers, research options, and make healthcare decisions. Be present and active.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-8 hover:shadow-xl transition-all duration-300 border border-gray-100"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-[#7C3AED] to-[#A855F7] rounded-lg flex items-center justify-center mb-6">
                  <i className={`${benefit.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="w-full h-[500px] rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://readdy.ai/api/search-image?query=Social%20media%20management%20dashboard%20showing%20content%20calendar%20post%20scheduling%20analytics%20and%20engagement%20metrics%20for%20healthcare%20practice%20professional%20social%20media%20tools%20interface%20purple%20accents%20clean%20modern%20design&width=600&height=700&seq=socialfeatures1&orientation=portrait"
                  alt="Social media features"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#7C3AED] font-bold mb-4">
                COMPREHENSIVE MANAGEMENT
              </div>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
                Full-Service Social Media Management
              </h2>
              <p className="text-base text-gray-600 leading-relaxed mb-8">
                From strategy to content creation to community management, we handle every aspect of your social media presence.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-5 h-5 bg-[#7C3AED]/10 rounded-full flex items-center justify-center mt-0.5 flex-shrink-0">
                      <i className="ri-check-line text-[#7C3AED] text-sm"></i>
                    </div>
                    <span className="text-gray-700 text-sm font-medium">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="process" className="py-24 px-6 lg:px-12 bg-[#F9FAFB]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="text-xs uppercase tracking-[0.2em] text-[#7C3AED] font-bold mb-4">
              OUR PROCESS
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
              Strategic Social Media That Drives Results
            </h2>
            <p className="text-base text-gray-600 max-w-3xl mx-auto">
              Our systematic approach ensures consistent, engaging content that builds your brand and attracts patients.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {process.map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-8 hover:shadow-lg transition-all duration-300 border border-gray-100"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-[#7C3AED] to-[#A855F7] rounded-xl flex items-center justify-center mb-6">
                  <span className="text-2xl font-bold text-white">{item.step}</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {item.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 lg:px-12 bg-gradient-to-br from-[#0D9488] to-[#14B8A6] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/10"></div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
            Ready to Build Your Social Media Presence?
          </h2>
          <p className="text-lg text-white/90 mb-8 leading-relaxed">
            Get a custom social media strategy showing exactly how we'll grow your following and attract more patients.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a
              href="/book-strategy-call"
              className="inline-flex items-center gap-2 bg-white text-[#0D9488] px-8 py-4 rounded-lg hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap font-bold"
            >
              <span>Get Your Social Strategy</span>
              <i className="ri-arrow-right-line"></i>
            </a>
            <a
              href="/#contact"
              className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 px-8 py-4 rounded-lg hover:bg-white/20 transition-all cursor-pointer whitespace-nowrap font-medium"
            >
              <span>See Content Examples</span>
            </a>
          </div>
        </div>
      </section>

      <RelatedServices currentSlug="social-media-marketing" />
      <RelatedIndustries />

      <Footer />
    </div>
  );
}
